import React, { isValidElement, useEffect, useState, useRef } from 'react';
import { toArray } from '../../utils/dom';
import styled from 'styled-components'
import { typeface, get_color, respond } from '../../assets/styles';
import imgChevronUpGreen from '../../assets/images/icn_stroke_chevron_up_hase_green.svg';
import imgChevronDownGreen from '../../assets/images/icn_stroke_chevron_down_hase_green.svg';
import imgChevronDownGrey from '../../assets/images/icn_stroke_chevron_down_grey02.svg';
import useResponse from '../../assets/hook/useResponse';

const TabContainer = styled.div`
  width: 100%;
  ${respond(['t'], `
    border: 1px solid ${get_color('grey06')};
  `)}
`

const TabButton = styled.button<{isActive?: boolean}>`
  width: 100%;
  display: flex;
  color: ${get_color('grey02')};

  .label{
    flex: 1;
  }

  :after {
    content: "";
    background-image: url(${props => props.isActive ? imgChevronUpGreen : imgChevronDownGrey});
    display: inline-block;
    width: 28px;
    height: 28px;
    background-size: cover;
    margin: 0 12px;
  }

  :hover {
    color: ${get_color('green01')};
    ${props => !props.isActive && 
      `
      background-color: ${get_color('grey09')};
      &:after{
        background-image: url(${imgChevronDownGreen});
      }`
    }
  }

  ${respond(['t'], `
    ${typeface('body7')}
    padding: 12px 20px 12px 20px;
    background-color: ${get_color('grey07')};
    border: none;
    cursor: pointer;
    border-bottom: 1px solid ${get_color('grey06')};
    position: relative;
    justify-content: center;
    align-items: flex-start;

    :not(:last-of-type) {
      border-right: 1px solid ${get_color('grey06')};
    }

    &:after{
      content: none;
    }
  `)}

  ${respond(['m', 's'], `
    ${typeface('title7')}
    padding: 10px 0;
    background-color: ${get_color('white')};
    border: none;
    cursor: pointer;
    position: relative;
    justify-content: start;
    align-items: center;
    border-radius: 5px 5px 0 0;
    text-align: left;
    height: 48px;
    .label{
      padding-left: 12px;
    }
  `)}

  ${props => props.isActive && `
      background-color: ${get_color('white')} !important;
      border-bottom: 1px solid ${get_color('white')}!important;
      color: ${get_color('green01')};

      ${respond(['t'], `
        ::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          height: 3px;
          width: 100%;
          background-color: ${get_color('green02')};
        }
      `)}
      ${respond(['m', 's'], `
        ::before {
          content: '';
          position: absolute;
          width: 3px;
          height: 48px;
          background-color: ${get_color('green02')};
          border-radius: 5px 0 0 0;
        }
      `)}
  `}
`

const TabButtonRow = styled.div`
  width: 100%;
  display: flex;

  .tab-button-container{
    border: 1px solid ${get_color('grey07')} !important;
    border-radius: 5px;
    overflow: hidden;
    &:not(:last-child) {
      margin-bottom: 16px;
    }
  }

  ${respond(['m', 's'], `
    flex-direction: column;
  `)}
`

const TabButtonFilter = styled.div`
  flex-grow: 1;
  border-bottom: ${get_color('grey07')} 1px solid;
`

const TabContent = styled.div<{isActive?: boolean, contentHeight?: number}>`
  width: auto;
  padding: 24px 24px 48px;
  display: flex;
  font-size: 16px;
  line-height: 30px;

  ${respond(['m', 's'], `
    border-top: ${get_color('grey07')} 1px solid;
    padding: 16px 12px 32px;
  `)}

  ${respond(['s'], `
    font-size: 15px;
    line-height: 24px;
  `)}
`

const TabContentContainer = styled.div<{isActive?: boolean, contentHeight?: number}>`
  transition: all 0.5s ease-in-out;
  overflow: hidden;
  height: ${props => props.isActive ? props.contentHeight + 'px' : '0'};
  ${props => props.isActive && 'border-radius: 0 0 5px 5px'};
`

export interface TabProps {
  defaultKey?: string;
  onChange?: (key?: React.Key) => void;
  children: React.ReactElement[];
  className?: string;
}

export interface TabPaneProps {
  title: string;
  key: React.Key;
}

export interface TabContentProps {
  isActive: boolean;
  children: React.ReactElement;
}

export const TabContentArea: React.FC<TabContentProps> = (props) => {
  const ref = useRef<HTMLDivElement>(null);
  const [bodyHeight, setBodyHeight] = useState<number>(0);

  const resizeHandler = () => {
    if (ref.current != null) {
      setBodyHeight(ref.current.clientHeight);
    }
  }

  useEffect(() => {
    window.addEventListener("resize", () =>{
      resizeHandler();
    })
    resizeHandler();
  },[])

  return (
    <TabContentContainer isActive={props.isActive} aria-hidden={!props.isActive} contentHeight={bodyHeight}>
      <TabContent ref={ref}>
        {props.children}
      </TabContent>
    </TabContentContainer>
  );
};

export const TabPane: React.FC<TabPaneProps> = (props) => {
  return <>{props.children}</>;
};

export const Tab: React.FC<TabProps> = (props) => {
  const { defaultKey, onChange, children, className } = props;
  const [activeKey, setActiveKey] = useState<React.Key | undefined>(defaultKey ?? undefined);
  const breakpoint = useResponse();

  const tabs = toArray(children)
    .filter((node) => isValidElement(node))
    .map((node: React.ReactElement<TabPaneProps>) => {
      return {
        ...node.props,
        node,
        key: node.key as React.Key | undefined,
      };
    })
    .filter((it) => it);

  useEffect(() => {
    breakpoint ==='t' && (activeKey || setActiveKey(tabs[0].key));
  }, [breakpoint, activeKey, tabs])

  useEffect(() => {
    onChange && onChange(activeKey);
  }, [activeKey, onChange]);

  const handleTabChange = (key?: React.Key) => {
    if(key !== activeKey) {
      setActiveKey(key);
    } else {
      if(breakpoint !== 't')
        setActiveKey('');
    }

  };

  return (
    <TabContainer className={className}>
      {breakpoint === 't' && (
        <>
          <TabButtonRow role='tablist'>
          {tabs.map((tab, idx) => (
            <TabButton
              aria-selected={tab.key === activeKey}
              key={idx}
              onClick={() => handleTabChange(tab.key)}
              isActive={tab.key === activeKey}
              role='tab'
            >
              <div className="label" role="heading" aria-level={3}>
                {tab.title}
              </div>
            </TabButton>
          ))}
          <TabButtonFilter/>
        </TabButtonRow>
        {tabs.filter((tab) => tab.key === activeKey).map((tab, idx) => 
          <TabContent key={idx}>{tab.node}</TabContent>
        )}
      </>
    )}
    {breakpoint !== 't' && (
      <TabButtonRow>
        {tabs.map((tab, idx) => (
          <div className="tab-button-container" key={idx}>
            <TabButton
              onClick={() => handleTabChange(tab.key)}
              isActive={tab.key === activeKey}
              aria-expanded={tab.key === activeKey}
            >
              <div className="label" role="heading" aria-level={3}>
                {tab.title}
              </div>
            </TabButton>
            <TabContentArea isActive={tab.key === activeKey}>
                {tab.node}
            </TabContentArea>
          </div>
        ))}
      </TabButtonRow>
    )}
    </TabContainer>
  );
};
